import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const projects = [
  {
    id: "credit-risk",
    index: "01",
    title: "Credit Risk Modeling",
    year: "2025",
    stack: ["Python", "Scikit-learn", "Streamlit"],
    tags: "PD · LGD · EAD · EL",
    description:
      "Preprocess raw banking data, then train machine-learning models to estimate Probability of Default, Loss Given Default and Exposure At Default — combining them into Expected Loss, a core metric banks use to lend profitably while keeping risk in check.",
    href: "https://credit-risk-model-cwfap5az9iifbiyzt84hfw.streamlit.app/",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBkYXRhJTIwZGFzaGJvYXJkfGVufDB8fHx8MTc4NTgzODQxNHww&ixlib=rb-4.1.0&q=85",
    align: "left",
  },
  {
    id: "neural-lab",
    index: "02",
    title: "CBP Forecast",
    year: "2025",
    stack: ["Deep Learning", "Forecasting", "Healthcare"],
    tags: "CBP disease forecasting",
    description:
      "A lab built to predict the progression of CBP disease — turning clinical signals into forward-looking projections that can support earlier, better-informed medical decisions.",
    href: "https://neural-lab-20.preview.emergentagent.com/?utm_source=share",
    image:
      "https://images.pexels.com/photos/3735715/pexels-photo-3735715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    align: "right",
  },
];

const ProjectCard = ({ p }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const isRight = p.align === "right";

  return (
    <div
      ref={ref}
      data-testid={`project-${p.id}`}
      className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-16"
    >
      {/* Image */}
      <motion.a
        href={p.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`project-image-link-${p.id}`}
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className={`group relative md:col-span-7 ${isRight ? "md:order-2 md:col-start-6" : "md:order-1"}`}
      >
        <div className="bg-white p-6 md:p-10 border border-[#E5E5DF]">
          <div className="relative aspect-[4/5] overflow-hidden md:aspect-[4/3]">
            <motion.img
              style={{ y: imgY, scale: 1.12 }}
              src={p.image}
              alt={p.title}
              className="absolute inset-0 h-full w-full object-cover grayscale transition-[filter] duration-700 group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-[#0A0A0A]/0 transition-colors duration-500 group-hover:bg-[#0A0A0A]/10" />
            <div className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center bg-[#F9F9F7] opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 translate-x-2">
              <ArrowUpRight strokeWidth={1.2} className="h-5 w-5" />
            </div>
          </div>
        </div>
      </motion.a>

      {/* Text */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className={`md:col-span-5 ${isRight ? "md:order-1 md:col-start-1 md:row-start-1" : "md:order-2"}`}
      >
        <div className="flex items-center gap-4">
          <span className="font-mono text-sm text-[#5C5C5C]">{p.index}</span>
          <span className="overline">{p.tags}</span>
        </div>

        <a href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`project-title-link-${p.id}`} className="group block">
          <h3 className="mt-6 font-display text-5xl font-medium leading-[0.95] tracking-tight md:text-7xl">
            <span className="bg-gradient-to-r from-[#0A0A0A] to-[#0A0A0A] bg-[length:0%_1px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 group-hover:bg-[length:100%_1px]">
              {p.title}
            </span>
          </h3>
        </a>

        <p className="mt-7 max-w-md font-sans text-base leading-relaxed text-[#5C5C5C]">
          {p.description}
        </p>

        <div className="mt-8 flex flex-wrap gap-x-3 gap-y-2">
          {p.stack.map((s) => (
            <span key={s} className="border border-[#E5E5DF] px-3 py-1 font-mono text-[0.65rem] uppercase tracking-[0.14em] text-[#5C5C5C]">
              {s}
            </span>
          ))}
        </div>

        <a
          href={p.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`project-cta-${p.id}`}
          className="group mt-10 inline-flex items-center gap-3 font-mono text-[0.72rem] uppercase tracking-[0.2em]"
        >
          <span className="relative">
            Launch project
            <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#0A0A0A] transition-all duration-500 group-hover:w-full" />
          </span>
          <ArrowUpRight strokeWidth={1.4} className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </a>
      </motion.div>
    </div>
  );
};

export const Projects = () => {
  return (
    <section id="work" data-testid="work-section" className="border-t border-[#E5E5DF]">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <div className="mb-20 flex items-end justify-between md:mb-32">
          <div>
            <span className="overline">Selected Work</span>
            <h2 className="mt-6 font-display text-6xl font-medium leading-none tracking-tight md:text-8xl">
              Projects
            </h2>
          </div>
          <span className="hidden font-mono text-sm text-[#5C5C5C] md:block">(02)</span>
        </div>

        <div className="flex flex-col gap-28 md:gap-48">
          {projects.map((p) => (
            <ProjectCard key={p.id} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
